import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Camera, Film, Palette, ImageIcon, ChevronRight } from "lucide-react";
import ServiceContactForm from "@/components/ServiceContactForm";

const servicesList = [
  {
    icon: Camera,
    title: "Photography Services",
    desc: "Our network of photographers covers every genre — from commercial and portrait to fashion, brand, bridal, and maternity photography.",
    bullets: ["Commercial & Product Photography", "Portrait & Headshot Sessions", "Fashion & Editorial Shoots", "Brand & Lifestyle Photography", "Bridal & Maternity Sessions"],
    price: "Starting at $150",
    cta: "View Photography Services",
    link: "/columbus-photography-service",
    image: "https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=800&q=80",
  },
  {
    icon: Film,
    title: "Film & Video Production",
    desc: "Full-service video production from concept to delivery. Our studio is built for cinematography, short films, brand videos, and social content.",
    bullets: ["Cinematography & Short Films", "Brand & Corporate Videos", "Social Media Content", "Music Videos", "Documentary Work"],
    price: "Starting at $150",
    cta: "View Video Production",
    link: "/production-service",
    image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80",
  },
  {
    icon: Palette,
    title: "Post Production",
    desc: "Professional photo editing, video editing, and color grading services with 10+ years of experience in the industry.",
    bullets: ["Photo Retouching & Color Correction", "Video Editing & Assembly", "Color Grading", "Motion Graphics", "Audio Mixing & Mastering"],
    price: "Starting at $150",
    cta: "View Post Production",
    link: "/post-production",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80",
  },
  {
    icon: ImageIcon,
    title: "Photo Restoration",
    desc: "Breathe new life into damaged, faded, or old photographs using advanced restoration software and meticulous techniques.",
    bullets: ["Damaged Photo Repair", "Color Restoration", "Scratch & Tear Removal", "Old Photo Digitization", "Enlargement & Enhancement"],
    price: "Contact for Quote",
    cta: "Request Restoration Quote",
    link: "/contact?service=restoration",
    image: "https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=800&q=80",
  },
];

const ServicesPage = () => (
  <div className="pt-20">
    <Helmet>
      <title>Production Services Columbus GA | Photo, Video, Editing</title>
      <meta name="description" content="In-house photography, video production, post production, and photo restoration services in Columbus, GA. Starting at $150." />
      <link rel="canonical" href="https://bookmystudiospace.com/services" />
      <meta property="og:title" content="Production Services Columbus GA | Photo, Video, Editing" />
      <meta property="og:description" content="In-house photo, video, post production and restoration services in Columbus, GA." />
      <meta property="og:url" content="https://bookmystudiospace.com/services" />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": servicesList.slice(0, 3).map((service, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Service",
            "serviceType": service.title,
            "provider": {
              "@type": "LocalBusiness",
              "name": "MyStudio",
              "url": "https://bookmystudiospace.com"
            },
            "areaServed": {
              "@type": "City",
              "name": "Columbus",
              "containedInPlace": {
                "@type": "State",
                "name": "Georgia"
              }
            },
            "description": service.desc,
            "image": service.image,
            "url": `https://bookmystudiospace.com${service.link}`
          }
        }))
      })}</script>
    </Helmet>
    <section className="py-20 lg:py-28 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">IN-HOUSE PRODUCTION SERVICES</h1>
        <p className="text-muted-foreground text-lg font-body">All in-house services start at $150</p>
      </div>
    </section>

    <section className="pb-20">
      <div className="container mx-auto px-4 space-y-16">
        {servicesList.map((s, i) => (
          <div key={s.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}>
            <div className={i % 2 === 1 ? "lg:order-2" : ""}>
              <s.icon className="w-10 h-10 text-primary mb-4" />
              <h2 className="text-3xl font-display tracking-wider mb-4">{s.title}</h2>
              <p className="text-muted-foreground font-body mb-6">{s.desc}</p>
              <ul className="space-y-2 mb-6">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm font-body">
                    <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="text-primary font-mono text-sm mb-6">{s.price}</p>
              <Link to={s.link}>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
                  {s.cta}
                </Button>
              </Link>
            </div>
            <div className={`aspect-video bg-panel overflow-hidden border border-border ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Contact Form */}
    <ServiceContactForm />
  </div>
);

export default ServicesPage;
